import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getActionItemState, setActionItemResponse, ActionItemKey, getAllActionItemStates, ActionItemStateByKey, NotificationResponse } from '@uplift/api';
import { CamelCasedPropertiesDeep } from 'type-fest';


export const useActionItem = <T extends ActionItemKey>(key: T) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: [ 'actionItem', key ] as const,
    queryFn: ({ queryKey: [ _, key ] }) => getActionItemState({ key })
  });

  const dismissMutation = useMutation({
    mutationFn: async (response: NotificationResponse = NotificationResponse.dismiss) => {
      const result = await setActionItemResponse(key, { response });

      queryClient.setQueryData([ 'actionItem', key ], result);

      return result;
    }
  });

  return {
    ...data?.data as CamelCasedPropertiesDeep<ActionItemStateByKey[T]>,
    isLoading,
    isDismissing: dismissMutation.isLoading,
    dismiss: dismissMutation.mutateAsync,
  };
};

export const useAllActionItems = () => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: [ 'allActionItems' ] as const,
    queryFn: getAllActionItemStates
  });

  type DismissInput = {
    key: ActionItemKey;
    response?: NotificationResponse;
    id?: string;
  } & Record<string, string>;

  const dismissMutation = useMutation({
    mutationFn: async ({ key, response = NotificationResponse.dismiss, ...rest }: DismissInput) => {
      const data = rest.id ? rest : undefined;

      await setActionItemResponse(key, { response, data });
    },
    onSuccess: () => queryClient.invalidateQueries([ 'allActionItems' ]),
  });

  return {
    data: data?.data ?? {} as CamelCasedPropertiesDeep<ActionItemStateByKey>,
    isLoading: isLoading || dismissMutation.isLoading,
    dismiss: dismissMutation.mutateAsync
  };
};
